import { useEffect, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronUp, Search, X } from 'lucide-react';
import type { ChatSummary, MessageDto } from '../api/client.ts';
import { formatDaySeparator, formatSearchCount, formatTime } from '../lib/format.ts';
import { searchable, snippetAround, splitHighlight } from '../lib/search.ts';
import { Avatar } from './Avatar.tsx';
import { ChatAvatar } from './ChatAvatar.tsx';
import './MessageSearch.css';

/**
 * «Поиск по сообщениям», from design/design_handoff_search: the field that
 * replaces the header, the list of matches under it, and the strip that walks
 * between them once one has been opened in the thread.
 *
 * Three exported pieces rather than one screen, because they are not on screen
 * together: the list stands where the thread does, and the strip where the
 * composer does. What holds them together is ChatScreen, which owns the query
 * and which match is being looked at.
 */

/** The header while searching: back, the field, and the cross that empties it. */
export function SearchField({
  query,
  onQuery,
  onBack,
}: {
  query: string;
  onQuery: (query: string) => void;
  onBack: () => void;
}) {
  const field = useRef<HTMLInputElement>(null);

  // The magnifier is asking for a word, so the keyboard comes up with the field
  // rather than after a second tap on it.
  useEffect(() => field.current?.focus(), []);

  return (
    <header className="search-header">
      <button type="button" className="icon-button search-back" onClick={onBack} aria-label="Назад">
        <ChevronLeft size={20} />
      </button>
      <div className="search-field">
        <Search className="search-field-icon" size={16} />
        <input
          ref={field}
          className="search-input"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder="Поиск в переписке"
          aria-label="Поиск в переписке"
          // A search field is not a word processor: nothing here should be
          // second-guessed by the keyboard on the way in.
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
        />
        {query.length > 0 && (
          <button
            type="button"
            className="search-clear"
            onClick={() => {
              onQuery('');
              field.current?.focus();
            }}
            aria-label="Очистить"
          >
            <X size={11} strokeWidth={3} />
          </button>
        )}
      </div>
    </header>
  );
}

/** One line of the message, with the words that were searched for marked in it. */
function Snippet({ body, query }: { body: string; query: string }) {
  return (
    <>
      {splitHighlight(snippetAround(body, query), query).map((run, index) =>
        run.hit ? (
          <mark key={index} className="text-hit">
            {run.value}
          </mark>
        ) : (
          <span key={index}>{run.value}</span>
        ),
      )}
    </>
  );
}

export function SearchResults({
  query,
  results,
  total,
  loading,
  hasMore,
  onLoadMore,
  onOpen,
}: {
  query: string;
  results: MessageDto[];
  total: number;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpen: (message: MessageDto) => void;
}) {
  if (!searchable(query)) {
    return (
      <div className="search-state">
        <span className="search-state-title">Поиск в переписке</span>
        <span className="search-state-text">
          Введите слово — покажем сообщения, где оно встречается
        </span>
      </div>
    );
  }

  // Only once the answer is in: redrawing «Ничего не найдено» between every
  // keystroke and its reply is the list flickering at somebody still typing.
  if (results.length === 0) {
    return loading ? (
      <div className="search-state">
        <span className="search-state-text">Ищем…</span>
      </div>
    ) : (
      <div className="search-state">
        <Search className="search-state-icon" size={30} strokeWidth={1.7} />
        <span className="search-state-title">Ничего не найдено</span>
        <span className="search-state-text">В этой переписке нет сообщений с «{query.trim()}»</span>
      </div>
    );
  }

  return (
    <div
      className="search-results"
      onScroll={(event) => {
        if (!hasMore || loading) return;
        const list = event.currentTarget;
        if (list.scrollTop + list.clientHeight > list.scrollHeight - 240) onLoadMore();
      }}
    >
      <div className="search-count">{formatSearchCount(total)}</div>
      {results.map((message) => (
        <button
          type="button"
          key={message.id}
          className="search-row"
          onClick={() => onOpen(message)}
        >
          <Avatar
            className="search-row-avatar"
            photoId={message.senderAvatarId}
            name={message.senderName}
          />
          <div className="search-row-body">
            <div className="search-row-head">
              <span className="search-row-who">{message.fromMe ? 'Вы' : message.senderName}</span>
              <span className="search-row-when">{formatDaySeparator(message.createdAt)}</span>
            </div>
            <div className="search-row-text">
              <Snippet body={message.body ?? ''} query={query} />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * The same matches from the field above the chat list, where they can come from
 * anywhere: the row is titled with the conversation rather than the sender,
 * because «в каком чате» is the question the list is being asked. In a group
 * the sender goes in front of the line, exactly as a chat-list snippet does it.
 *
 * Rendered inside the chat list's own scroller rather than beside it — one
 * column of results under one heading, and one thing to scroll.
 */
export function ChatMatches({
  query,
  results,
  total,
  hasMore,
  onLoadMore,
  chats,
  onOpen,
}: {
  query: string;
  results: MessageDto[];
  total: number;
  hasMore: boolean;
  onLoadMore: () => void;
  /** The loaded conversations, for the name and the picture of each match. */
  chats: ChatSummary[];
  onOpen: (message: MessageDto) => void;
}) {
  if (results.length === 0) return null;

  return (
    <>
      <div className="search-count">{formatSearchCount(total)}</div>
      {results.map((message) => {
        const chat = chats.find((item) => item.id === message.conversationId);
        return (
          <button
            type="button"
            key={message.id}
            className="search-row"
            onClick={() => onOpen(message)}
          >
            <ChatAvatar chat={chat} className="search-row-avatar" glyph={18} />
            <div className="search-row-body">
              <div className="search-row-head">
                <span className="search-row-who">{chat?.title ?? 'Переписка'}</span>
                {/* Here and not in the list below the «БОТЫ» divider, where
                    every row is one: results are people and bots mixed, and a
                    name on its own says nothing about which this is. */}
                {chat?.bot && <span className="search-row-bot">БОТ</span>}
                <span className="search-row-when">{formatDaySeparator(message.createdAt)}</span>
              </div>
              <div className="search-row-text">
                {chat?.kind === 'group' && (
                  <span className="search-row-sender">
                    {message.fromMe ? 'Вы' : message.senderName}:{' '}
                  </span>
                )}
                <Snippet body={message.body ?? ''} query={query} />
              </div>
            </div>
          </button>
        );
      })}
      {hasMore && (
        <button type="button" className="search-more" onClick={onLoadMore}>
          Показать ещё
        </button>
      )}
    </>
  );
}

/**
 * The strip under a thread showing a match: which of them is open, when it was
 * written, and the two arrows that step to the next one.
 *
 * Up is older and down is newer, matching the thread it steps through rather
 * than the list, which is the other way round.
 */
export function SearchNavBar({
  position,
  total,
  createdAt,
  onOlder,
  onNewer,
}: {
  /** Which match is open, counting from 1 in the order the list shows them. */
  position: number;
  total: number;
  createdAt: number;
  onOlder: (() => void) | null;
  onNewer: (() => void) | null;
}) {
  return (
    <div className="search-nav">
      <div className="search-nav-place">
        <span className="search-nav-count">
          {position} из {total}
        </span>
        <span className="search-nav-when">
          {formatDaySeparator(createdAt)}, {formatTime(createdAt)}
        </span>
      </div>
      <button
        type="button"
        className="icon-button search-nav-step"
        onClick={() => onOlder?.()}
        disabled={onOlder === null}
        aria-label="Предыдущее совпадение"
      >
        <ChevronUp size={17} />
      </button>
      <button
        type="button"
        className="icon-button search-nav-step"
        onClick={() => onNewer?.()}
        disabled={onNewer === null}
        aria-label="Следующее совпадение"
      >
        <ChevronDown size={17} />
      </button>
    </div>
  );
}
